// Forced password change modal component
// Displays when user must change their password (first login or admin reset)
// Cannot be dismissed until password is changed
// CHANGE: Added password validation and requirements checklist
// UPDATED: Migrated to centralized toast system for error messages
// UPDATED: Standardized button loading text to "En cours..."

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { getPortalRoot } from '../lib/portalUtils';
import { changePassword } from '../lib/auth';
import { validatePassword } from '../lib/passwordPolicy';
import { PasswordRequirementsChecklist } from './ui/PasswordRequirementsChecklist';
import { useToast } from '../hooks/useToast';

interface ForcedPasswordChangeModalProps {
  onSuccess: () => void;
  reason?: 'first_login' | 'admin_reset';
}

export function ForcedPasswordChangeModal({ onSuccess, reason = 'first_login' }: ForcedPasswordChangeModalProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  const getTitle = () => {
    switch (reason) {
      case 'admin_reset':
        return 'Votre mot de passe a été réinitialisé';
      case 'first_login':
      default:
        return 'Première connexion - Changement de mot de passe requis';
    }
  };

  const getMessage = () => {
    switch (reason) {
      case 'admin_reset':
        return 'Un administrateur a réinitialisé votre mot de passe. Pour des raisons de sécurité, vous devez définir un nouveau mot de passe avant de continuer.';
      case 'first_login':
      default:
        return 'Pour des raisons de sécurité, vous devez changer votre mot de passe lors de votre première connexion.';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password confirmation
    if (newPassword !== confirmPassword) {
      showToast('Les mots de passe ne correspondent pas', 'error');
      return;
    }

    // Validate password against policy
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      showToast(validation.errors[0], 'error');
      return;
    }

    setLoading(true);
    const result = await changePassword(newPassword, true);

    if (result.success) {
      onSuccess();
    } else {
      showToast(result.error || 'Une erreur est survenue', 'error');
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[70]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="text-amber-600" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{getTitle()}</h3>
              <p className="text-sm text-gray-600 mt-1">{getMessage()}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Nouveau mot de passe *
            </label>
            <div className="relative">
              <input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
                className="w-full px-4 py-3 pr-32 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="Minimum 6 caractères"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                disabled={loading}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {showNewPassword ? (
                  <>
                    <EyeOff size={18} />
                    <span>Masquer</span>
                  </>
                ) : (
                  <>
                    <Eye size={18} />
                    <span>Afficher</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Password requirements checklist */}
          {newPassword && (
            <PasswordRequirementsChecklist password={newPassword} />
          )}

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Confirmer le mot de passe *
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                className="w-full px-4 py-3 pr-32 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="Confirmez votre mot de passe"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {showConfirmPassword ? (
                  <>
                    <EyeOff size={18} />
                    <span>Masquer</span>
                  </>
                ) : (
                  <>
                    <Eye size={18} />
                    <span>Afficher</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'En cours...' : 'Changer mon mot de passe'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    getPortalRoot()
  );
}
